import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Lock, ArrowRight, Sparkle, CheckCircle } from "@phosphor-icons/react";

const perks = [
  "Unlimited load screenings",
  "Side-by-side load comparison",
  "Weekly analytics dashboard",
  "Branded PDF exports",
  "Load history & audit trail",
];

export const PaywallDialog = ({ open, onOpenChange, mode = "guest" }) => {
  const nav = useNavigate();
  const title =
    mode === "guest"
      ? "You've used your 3 free loads today"
      : "Upgrade to Pro";
  const subtitle =
    mode === "guest"
      ? "Create a free account to keep screening loads, or upgrade to Pro for unlimited + analytics."
      : "Unlock unlimited screenings, weekly analytics and branded PDF exports.";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="paywall-dialog"
        className="max-w-lg bg-white p-0 overflow-hidden"
      >
        <div className="bg-[#0a192f] text-white p-6 md:p-8 relative overflow-hidden">
          <div
            aria-hidden
            className="absolute -top-16 -end-16 w-64 h-64 rounded-full blur-3xl opacity-40"
            style={{
              background:
                "radial-gradient(closest-side, rgba(16,185,129,0.6), transparent 70%)",
            }}
          />
          <div className="relative">
            <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-400/30 grid place-items-center">
              <Lock size={20} weight="duotone" className="text-emerald-400" />
            </div>
            <DialogHeader>
              <DialogTitle className="mt-5 text-2xl md:text-3xl font-semibold tracking-tight text-white">
                {title}
              </DialogTitle>
            </DialogHeader>
            <p className="mt-2 text-slate-300 text-sm">{subtitle}</p>
          </div>
        </div>

        <div className="p-6 md:p-8 bg-white">
          <div className="flex items-baseline gap-2">
            <Sparkle size={16} weight="fill" className="text-emerald-500" />
            <p className="text-[10px] uppercase tracking-[0.16em] text-emerald-700 font-semibold">
              Pro plan
            </p>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="tabular text-4xl font-semibold text-slate-900">
              $19
            </span>
            <span className="text-sm text-slate-500">/ month per user</span>
          </div>

          <ul className="mt-5 space-y-2">
            {perks.map((p) => (
              <li key={p} className="flex items-start gap-2 text-sm text-slate-700">
                <CheckCircle
                  size={16}
                  weight="fill"
                  className="text-emerald-500 mt-0.5 shrink-0"
                />
                {p}
              </li>
            ))}
          </ul>

          <div className="mt-6 flex flex-col sm:flex-row gap-2">
            <Button
              data-testid="paywall-signup"
              onClick={() => nav("/signup")}
              className="btn-neon flex-1 rounded-xl h-11 text-sm"
            >
              Create free account
              <ArrowRight size={16} weight="bold" className="flip-x ms-1" />
            </Button>
            <Button
              data-testid="paywall-billing"
              onClick={() => nav("/app/billing")}
              variant="outline"
              className="flex-1 rounded-xl h-11 border-slate-300 text-sm"
            >
              Upgrade to Pro
            </Button>
          </div>

          <p className="mt-4 text-xs text-slate-500 text-center">
            Already have an account?{" "}
            <button
              data-testid="paywall-login"
              onClick={() => nav("/login")}
              className="text-emerald-600 hover:text-emerald-700 underline underline-offset-2"
            >
              Sign in
            </button>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
